import React from 'react'
import styled from 'styled-components'
import moment from 'moment'

import { ShougaiMimaiKinData } from '../../../../types/formData'

import { Heading } from '../../../ui/Heading'
import { Input } from '../../../ui/Input'
import { RadioButtonLabel } from '../../../ui/RadioButtonLabel'
import { DatePicker } from '../../../ui/DatePicker'
import { Controller, useFormContext } from 'react-hook-form'
import {
  BankAccountSection,
  FileSection,
  MemberInfoSection,
} from './inputParts'
import { MessageColumnSection } from '../../../layout/inputParts/MessageColumnSection'
import { mediaQuery } from '../../../../themes/size'
import { isYYYYMMDD } from '../../../../util/validation'

type Props = {
  disabled: boolean
  handleSaveImage?: (files: File) => Promise<void>
  isUploading?: boolean
  handleDeleteImage?: (fileId: string) => Promise<void>
  canAdminUpload?: boolean
  shouldShowOtherFormLinks?: true
}

const Component: React.FC<Props> = (props) => {
  const methods = useFormContext<ShougaiMimaiKinData['formData']>()
  const { control, errors, setValue, getValues } = methods

  const handleChangeBirthDay = (birthDay: Date | null) => {
    // 生年月日セット
    setValue('birthDay', birthDay)

    // 生年月日が未選択の場合、請求金額は設定しない
    if (!birthDay) {
      setValue('amount', '')
      return
    }

    // 障害等級取得
    const grade = getValues('disabilityGrade')

    // 請求金額計算・セット
    calcAmount(grade, birthDay)
  }

  const handleChangeDisabilityGrade = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const grade = e.currentTarget.value
    // 障害等級セット
    setValue('disabilityGrade', grade)

    // 生年月日が未選択の場合、請求金額は設定しない
    const birthDay = getValues('birthDay')
    if (!birthDay) {
      setValue('amount', '')
      return
    }

    // 請求金額計算・セット
    calcAmount(grade, birthDay)
  }

  const calcAmount = (grade: string, birthDay: Date) => {
    // 71歳以上の会員の場合、等級に関わらず20,000円
    if (71 <= calcAge(birthDay)) {
      setValue('amount', '20,000')
    } else {
      switch (grade) {
        case 'gradeOne':
          setValue('amount', '70,000')
          break
        case 'gradeTwo':
          setValue('amount', '50,000')
          break
        case 'gradeThree':
          setValue('amount', '30,000')
          break
        default:
          setValue('amount', '')
      }
    }
  }

  const calcAge = (brithDay: Date) => {
    return moment().diff(moment(brithDay), 'years')
  }

  return (
    <>
      <HeadingArea>
        <Heading type="screenTitle" tag="h1">
          ▼給付金申請 - 障害見舞金
        </Heading>
      </HeadingArea>

      {Object.keys(methods.errors).length > 0 && (
        <Section>
          <ErrorMessage>
            入力内容に不備があります。各入力項目のメッセージを参照してください。
          </ErrorMessage>
        </Section>
      )}

      <MemberInfoSection
        shouldShowOtherFormLinks={props.shouldShowOtherFormLinks}
      />

      <Section>
        <Heading type="sectionTitle" tag="h2">
          ■申請情報
        </Heading>
        <InputField>
          <Heading type="blockTitle" tag="span">
            生年月日
          </Heading>
          <Controller
            control={control}
            name="birthDay"
            rules={{
              required: '生年月日を入力してください',
              validate: { isYYYYMMDD },
            }}
            render={({ value, onBlur }) => (
              <DatePicker
                onChange={handleChangeBirthDay}
                value={value}
                disabled={props.disabled}
                openToDate={true}
                onBlur={onBlur}
              />
            )}
          />
        </InputField>
        {errors.birthDay && (
          <ErrorMessage>{errors.birthDay.message}</ErrorMessage>
        )}
        {errors.birthDay?.type === 'isYYYYMMDD' && (
          <ErrorMessage>YYYY/MM/DDの形式で入力して下さい</ErrorMessage>
        )}
        <InputField>
          <Heading type="blockTitle" tag="span">
            障害者手帳取得日
          </Heading>
          <Controller
            control={control}
            name="disabilityCertificateDate"
            rules={{
              required: '障害者手帳取得日を入力してください',
              validate: { isYYYYMMDD },
            }}
            render={({ onChange, value, onBlur }) => (
              <DatePicker
                onChange={onChange}
                value={value}
                disabled={props.disabled}
                onBlur={onBlur}
              />
            )}
          />
        </InputField>
        {errors.disabilityCertificateDate && (
          <ErrorMessage>
            {errors.disabilityCertificateDate.message}
          </ErrorMessage>
        )}
        {errors.disabilityCertificateDate?.type === 'isYYYYMMDD' && (
          <ErrorMessage>YYYY/MM/DDの形式で入力して下さい</ErrorMessage>
        )}
        <InputField>
          <Heading type="blockTitle" tag="span">
            障害等級
          </Heading>
          <Controller
            control={control}
            name="disabilityGrade"
            defaultValue="gradeOne"
            render={({ value }) => (
              <RadioButtonWrapper>
                <RadioButtonArea>
                  <RadioButtonLabel
                    label="一級"
                    value="gradeOne"
                    checked={value === 'gradeOne'}
                    onChange={(e) => handleChangeDisabilityGrade(e)}
                    disabled={props.disabled}
                  />
                </RadioButtonArea>
                <RadioButtonArea>
                  <RadioButtonLabel
                    label="二級"
                    value="gradeTwo"
                    checked={value === 'gradeTwo'}
                    onChange={(e) => handleChangeDisabilityGrade(e)}
                    disabled={props.disabled}
                  />
                </RadioButtonArea>
                <RadioButtonArea>
                  <RadioButtonLabel
                    label="三級"
                    value="gradeThree"
                    checked={value === 'gradeThree'}
                    onChange={(e) => handleChangeDisabilityGrade(e)}
                    disabled={props.disabled}
                  />
                </RadioButtonArea>
              </RadioButtonWrapper>
            )}
          />
        </InputField>
        {errors.disabilityGrade && (
          <ErrorMessage>{errors.disabilityGrade.message}</ErrorMessage>
        )}
        <InputField>
          <Heading type="blockTitle" tag="span">
            請求金額
          </Heading>
          <LabelWrapper>
            <Controller
              control={control}
              name="amount"
              render={({ onChange, value }) => (
                <Input type="text" onChange={onChange} value={value} disabled />
              )}
            />
            <InputLabel>円</InputLabel>
          </LabelWrapper>
        </InputField>
      </Section>

      <FileSection
        headingLabel="身体障害者手帳"
        disabled={props.disabled}
        canAdminUpload={props.canAdminUpload}
        handleSaveImage={props.handleSaveImage}
        isUploading={props.isUploading}
        handleDeleteImage={props.handleDeleteImage}
      />

      <BankAccountSection disabled={props.disabled} isMember={true} />

      <MessageColumnSection disabled={props.disabled} />
    </>
  )
}

const HeadingArea = styled.div`
  margin-top: 32px;
`
// @fixme 子コンポーネントから共通で参照できるように一旦exportしました
export const Section = styled.section`
  margin-top: 72px;

  @media screen and (max-width: ${mediaQuery.SP}px) {
    margin-top: 42px;
  }
`
// @fixme 子コンポーネントから共通で参照できるように一旦exportしました
export const InputField = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 510px;
  margin-top: 16px;

  @media screen and (max-width: ${mediaQuery.SP}px) {
    display: block;
    width: 100%;
    & > span {
      &:first-child {
        margin-bottom: 8px;
      }
    }
  }
`
const ErrorMessage = styled.div`
  color: red;
`

const RadioButtonWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;

  @media screen and (max-width: ${mediaQuery.SP}px) {
    display: block;
  }
`
const RadioButtonArea = styled.div`
  display: flex;
  align-items: center;
  width: 110px;

  @media screen and (max-width: ${mediaQuery.SP}px) {
    margin-top: 8px;
  }
`
const LabelWrapper = styled.div`
  display: flex;
  align-items: center;
  width: 328px;
`
const InputLabel = styled.label`
  margin-left: 8px;
  font-size: 16px;
`

export default Component
